/**
 * Плавающая консоль Eruda.
 * `enable_eruda=1` — всегда включена, `0` — всегда выключена.
 * Если ключ не задан, в development консоль включается сама.
 */
export const ERUDA_STORAGE_KEY = "enable_eruda"

type ErudaApi = {
  init: (options?: { container?: HTMLElement }) => void
  destroy: () => void
  show?: () => void
}

type ErudaWindow = Window & {
  __erudaInitialized?: boolean
  eruda?: ErudaApi
}

let initPromise: Promise<void> | null = null

export function isErudaEnabled(): boolean {
  if (typeof window === "undefined") return false
  const stored = window.localStorage.getItem(ERUDA_STORAGE_KEY)
  if (stored === "0") return false
  if (stored === "1") return true
  return process.env.NODE_ENV !== "production"
}

async function initEruda(): Promise<void> {
  const win = window as ErudaWindow
  if (win.__erudaInitialized) return
  if (initPromise) return initPromise

  initPromise = (async () => {
    const mod = await import("eruda")
    const eruda = (mod.default ?? mod) as ErudaApi
    if (win.__erudaInitialized) return
    if (!document.getElementById("eruda-container")) {
      const container = document.createElement("div")
      container.id = "eruda-container"
      document.body.appendChild(container)
    }
    eruda.init({ container: document.getElementById("eruda-container") as HTMLElement })
    win.eruda = eruda
    win.__erudaInitialized = true
  })()

  try {
    await initPromise
  } finally {
    initPromise = null
  }
}

export async function setErudaEnabled(enabled: boolean): Promise<void> {
  const win = window as ErudaWindow
  if (enabled) {
    await initEruda()
    window.localStorage.setItem(ERUDA_STORAGE_KEY, "1")
    return
  }

  try {
    if (win.eruda) {
      win.eruda.destroy()
    } else {
      const mod = await import("eruda")
      const eruda = (mod.default ?? mod) as ErudaApi
      eruda.destroy()
    }
  } catch {
    // консоль могла быть ещё не инициализирована
  }
  win.__erudaInitialized = false
  win.eruda = undefined
  document.getElementById("eruda-container")?.remove()
  window.localStorage.setItem(ERUDA_STORAGE_KEY, "0")
}

/** Поднять консоль при старте, если пользователь её не выключал. */
export function applyErudaPreference(): void {
  if (!isErudaEnabled()) return
  initEruda().catch(() => {})
}
